import React from 'react';

const RDMACluster = () => (
  <article className="news-article-body">
    <p className="news-lead">
      Training a frontier AI model requires thousands of GPUs working in tight coordination.
      Every millisecond a GPU spends waiting for data instead of computing is wasted. This
      is why the networking layer - specifically RDMA over converged Ethernet - has become
      one of the most important engineering challenges in modern AI infrastructure, and why
      companies that solve it well have a real competitive advantage.
    </p>

    <h2>What is RDMA?</h2>
    <p>
      RDMA stands for Remote Direct Memory Access. It lets one server read or write directly
      into another server's memory over the network - without involving either server's CPU.
      Why does that matter? Because it makes GPU-to-GPU communication dramatically faster
      and more efficient.
    </p>
    <p>
      In a normal network transfer, data goes: GPU memory → CPU → network card → wire → network
      card → CPU → GPU memory. That's four unnecessary handoffs. With RDMA, the GPU can
      effectively reach across the network and grab data itself. Latency drops. CPU overhead
      drops. GPUs stay busy.
    </p>

    <h2>RoCE vs InfiniBand - what's the difference?</h2>
    <p>
      There are two main ways to run RDMA at scale:
    </p>
    <ul>
      <li>
        <strong>InfiniBand (IB):</strong> NVIDIA's proprietary networking technology, used
        in most serious AI clusters. Very high bandwidth, very low latency. The downside:
        you're locked into NVIDIA hardware, and it's expensive.
      </li>
      <li>
        <strong>RoCE (RDMA over Converged Ethernet):</strong> RDMA running over standard
        Ethernet infrastructure. More flexible, compatible with existing kit. The downside:
        harder to tune correctly, more sensitive to packet loss.
      </li>
    </ul>
    <p>
      Most hyperscalers (Google, Amazon, Meta) are building large RoCE networks because
      they already own Ethernet infrastructure and want to reduce InfiniBand dependency.
      NVIDIA still dominates InfiniBand, but the momentum is shifting toward Ethernet at scale.
    </p>

    <h2>How Meta used RDMA to build a 35,000-GPU cluster</h2>
    <p>
      Meta published details of their RSVP (Reliable, Scalable, Verified, and Performant)
      network, which runs 35,000 NVIDIA H100 GPUs over RoCE. Key engineering choices:
    </p>
    <ul>
      <li>Purpose-built congestion control algorithms to prevent packet loss across thousands of simultaneous flows</li>
      <li>Custom network topology to minimise cross-rack traffic</li>
      <li>Dedicated "fabric" networks separating AI training traffic from normal data traffic</li>
    </ul>
    <p>
      The result: GPU utilisation above 95% during training runs. For scale, training
      Llama 3 400B required keeping 16,000+ GPUs busy simultaneously.
    </p>

    <h2>What it means for competitive advantage</h2>
    <p>
      Companies that figure out RDMA at scale train models faster and cheaper. Microsoft,
      Google, Amazon, and Meta have all invested heavily in custom networking infrastructure.
      This is one reason why "just buying more GPUs" doesn't automatically mean better AI -
      the networking between GPUs matters just as much as the GPUs themselves.
    </p>

    <div className="news-insight-box">
      <h3>The big lesson</h3>
      <p>
        AI infrastructure is a whole-system problem - compute, memory, networking, and storage
        all have to scale together. The companies that win long-term aren't just the ones with
        the most GPUs. They're the ones who've engineered every layer of the stack to keep
        those GPUs working at maximum efficiency.
      </p>
    </div>
  </article>
);

export default RDMACluster;
